import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SectionList } from 'react-native';
import Animated from 'react-native-reanimated';
import AddAccountModal from 'features/accounts/components/AddAccountModal';
import { AppView, HelpCallToAction, rem, withAnimatedNavigationHeader } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import { getBankConnections } from 'actions';
import { muteAll, useFetchOld } from 'hooks';
import Links from 'utils/links';
import orderBankLoginsByPriority from 'utils/api/orderBankLoginsByPriority';
import { isBankLoginConnection } from 'utils/creditReportAccounts';
import useColors from 'hooks/useColors';
import usePlaidLink from 'hooks/usePlaidLink';
import { useAppSelector } from 'store/hooks';
import { selectConnections } from 'reducers/selectors';
import StatusBar from 'design-system/StatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import ListEmptyView from 'design-system/ListEmptyView';
import { selectUserTier } from 'features/premium/selectors/tier';
import { selectConnectionsLimit } from 'features/premium/selectors';
import FlatListItemCard from 'design-system/FlatListItemCard';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import SectionHeader from 'design-system/SectionHeader';
import LoginsHeader from './LoginsHeader';
import BankConnectionRow from './components/BankConnectionRow';
import { isConnectionBroken } from './utils';
import { isWeb } from '../../../constants';
const keyExtractor = (item, index) => (item.id ?? index).toString();
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
const Logins = withAnimatedNavigationHeader(({ navigation, onScroll, route }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.BANK_CONNECT}/>,
        });
    }, [colors.text.primary]);
    const connections = useAppSelector(selectConnections);
    const [disableCTA, setDisableCTA] = useState(false);
    const [, , , fetch] = useFetchOld(muteAll);
    const openPlaid = usePlaidLink();
    const timeoutRef = useRef(undefined);
    useEffect(() => () => clearTimeout(timeoutRef.current), []);
    const openAddAccount = useCallback(() => {
        AddAccountModal.show({
            navigate: navigation.navigate,
            onOpenPlaid: () => {
                if (disableCTA)
                    return;
                setDisableCTA(true);
                openPlaid();
                timeoutRef.current = setTimeout(() => {
                    setDisableCTA(false);
                }, 1000);
            },
            onAddOffline: () => navigation.navigate('PickAccountType', {}),
        });
    }, [disableCTA, openPlaid]);
    useEffect(() => {
        fetch(getBankConnections());
    }, []);
    const allConnections = useMemo(() => orderBankLoginsByPriority(connections.filter((item) => item.id), false).filter(isBankLoginConnection), [connections]);
    const connectionsMap = useMemo(() => {
        const active = [];
        const inactive = [];
        allConnections.forEach((conn) => {
            if (conn.deactivatedOverQuota) {
                inactive.push(conn);
            }
            else {
                active.push(conn);
            }
        });
        return { ACTIVE: active, INACTIVE: inactive };
    }, [allConnections]);
    const [selectedFilter, setSelectedFilter] = useState(route.params?.tab === 'inactive' ? 'INACTIVE' : 'ACTIVE');
    const connectionsArray = connectionsMap[selectedFilter];
    const ListHeaderComponent = useMemo(() => (<LoginsHeader showInfo={!!connectionsArray.length} connectionsMap={connectionsMap} selectedFilter={selectedFilter} onSelect={setSelectedFilter}/>), [connectionsArray, selectedFilter, connectionsMap]);
    const tier = useAppSelector(selectUserTier);
    const tiersLimitMap = useAppSelector(selectConnectionsLimit);
    const currentLimit = tiersLimitMap ? tiersLimitMap[tier] : 2;
    const renderEmpty = useCallback(() => {
        const title = selectedFilter === 'INACTIVE' ? 'All your connections are active!' : 'Looking empty...';
        const activeSubtitle = currentLimit === null
            ? 'Your plan gives you access to unlimited bank connections. Add as many as you want and track your spending in Emma!'
            : `Your plan gives you access to ${currentLimit} bank connections. Connect your bank and track your spending in Emma!`;
        const subtitle = selectedFilter === 'INACTIVE' ? "You don't have any inactive bank connections at the moment." : activeSubtitle;
        return (<ListEmptyView happy={selectedFilter === 'INACTIVE'} title={title} subTitle={subtitle} onCard compact style={styles.emptyContainer}/>);
    }, [currentLimit, selectedFilter, styles.emptyContainer]);
    const renderSectionHeader = useCallback(({ section }) => {
        if (section.data.length && !section.title)
            return <Spacer.H16 />;
        if (!section.data.length || !section.title)
            return null;
        return (<SectionHeader horizontalMargins largeVerticalMargins toolTipTitle={section.toolTipTitle} toolTipSubTitle={section.toolTipSubTitle}>
          {section.title}
        </SectionHeader>);
    }, []);
    const renderItem = useCallback(({ item, section: { data }, index }) => (<FlatListItemCard style={styles.card} index={index} itemsLength={data.length} onPress={() => navigation.navigate('BankConnection', { connectionId: item.id })}>
          <BankConnectionRow data={item} showStatus={selectedFilter === 'ACTIVE'}/>
        </FlatListItemCard>), [selectedFilter, styles.card]);
    const onHelpPress = useCallback(() => {
        Links.openKnownLink('bankLoginsHelp');
    }, []);
    const renderFooter = useMemo(() => (<>
          <Spacer.H16 />
          <HelpCallToAction onDark onPress={onHelpPress} subTitle="Learn how to do it" title="Need to amend your bank connections?"/>
        </>), [onHelpPress]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const activeSections = useMemo(() => {
        const connections = connectionsMap.ACTIVE;
        if (!connections.length)
            return [];
        const activeConnections = [];
        const closedConnections = [];
        connections.forEach((conn) => {
            if (conn.isClosed) {
                closedConnections.push(conn);
            }
            else {
                activeConnections.push(conn);
            }
        });
        activeConnections.sort((a, b) => {
            if (isConnectionBroken(a) !== isConnectionBroken(b)) {
                return isConnectionBroken(a) ? -1 : 1;
            }
            return 0;
        });
        return [
            { data: activeConnections },
            {
                data: closedConnections,
                title: 'Closed connections',
                toolTipTitle: 'What are closed connections?',
                toolTipSubTitle: 'Closed connections don’t sync anymore, but their data shows across the app. You can mark your bank connection as closed once you have closed your bank, so you don’t lose your data.',
            },
        ];
    }, [connectionsMap.ACTIVE]);
    const inactiveSections = useMemo(() => {
        const connections = connectionsMap.INACTIVE;
        if (!connections.length)
            return [];
        return [{ data: connections, key: 'INACTIVE' }];
    }, [connectionsMap.INACTIVE]);
    return (<AppView type="secondary" withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <AnimatedSectionList contentContainerStyle={paddingBottom} stickySectionHeadersEnabled={false} sections={selectedFilter === 'ACTIVE' ? activeSections : inactiveSections} renderItem={renderItem} renderSectionHeader={renderSectionHeader} ListEmptyComponent={renderEmpty} keyExtractor={keyExtractor} ListHeaderComponent={ListHeaderComponent} onScroll={onScroll} ListFooterComponent={renderFooter}/>
        <Button brand floating onPress={openAddAccount} title="Add a connection"/>
      </AppView>);
}, ({ colors }) => ({
    title: 'Bank connections',
    headerTintColor: colors.text.primary,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        backgroundColor: colors.cards.onDark,
    },
    emptyContainer: {
        marginTop: rem(16),
    },
}));
export default Logins;
